<template>
  <Transition name="fade">
    <div v-if="isPageLoading"
      class="fixed inset-0 z-50 flex items-center justify-center bg-white/80 dark:bg-gray-900/80 backdrop-blur-sm">
      <div class="flex flex-col items-center space-y-6 px-4">
        <!-- Gradient ring spinner -->
        <div class="relative w-16 h-16">
          <div class="absolute inset-0 rounded-full spinner-ring"></div>
          <div class="absolute inset-0 rounded-full spinner-pulse"></div>
        </div>

        <!-- Loading Text -->
        <div class="text-center space-y-3">
          <h3 class="text-lg font-semibold text-gray-900 dark:text-white">
            {{ loadingText }}
          </h3>

          <!-- Gradient progress bar -->
          <div class="flex justify-center items-center">
            <div class="w-64 h-1.5 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
              <div class="h-full rounded-full animate-loading-bar progress-gradient"></div>
            </div>
          </div>
          <p class="text-sm text-gray-500 dark:text-gray-400">
            Please wait...
          </p>
        </div>
      </div>
    </div>
  </Transition>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { useLoadingStore } from '@/stores/loading'

const loadingStore = useLoadingStore()

const isPageLoading = computed(() => loadingStore.isPageLoading)
const loadingText = computed(() => loadingStore.loadingText)
</script>

<style scoped>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

/* Spinner ring — uses primary color via mask */
.spinner-ring {
  background: rgb(var(--color-primary-light-DEFAULT));
  mask: radial-gradient(transparent 58%, black 59%);
  -webkit-mask: radial-gradient(transparent 58%, black 59%);
  animation: spin 1.2s cubic-bezier(0.68, -0.55, 0.265, 1.55) infinite;
}

/* Subtle pulse glow behind the spinner */
.spinner-pulse {
  background: rgb(var(--color-primary-light-DEFAULT));
  opacity: 0.15;
  animation: pulse 2s ease-in-out infinite;
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }

  100% {
    transform: rotate(360deg);
  }
}

@keyframes pulse {

  0%,
  100% {
    transform: scale(1);
    opacity: 0.15;
  }

  50% {
    transform: scale(1.3);
    opacity: 0.3;
  }
}

/* Progress bar — uses primary color */
.progress-gradient {
  background: rgb(var(--color-primary-light-DEFAULT));
}

@keyframes loading-bar {
  0% {
    transform: translateX(-100%);
  }

  50% {
    transform: translateX(0%);
  }

  100% {
    transform: translateX(100%);
  }
}

.animate-loading-bar {
  animation: loading-bar 2s ease-in-out infinite;
}
</style>
